import { useEffect, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { MousePositionContextMenu } from "@/components/sidebar/context-menu/MousePositionContextMenu";
import { useStore } from "@/store";
import {
  RECENT_DOCUMENTS_COLLAPSED_COUNT,
  RECENT_DOCUMENTS_MIN_TEAM_DOCUMENTS,
} from "@/store/RecentDocumentsStore";
import { useRecentDocuments } from "@/store/useRecentDocuments";
import { docPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import { SectionDivider } from "../SectionDivider";
import { SidebarSectionHeader } from "../SidebarSectionHeader";
import { useRowFocusWithin } from "../useRowFocusWithin";
import { NavSection, TreeItem } from "../useSidebarNav";

import { ContextMenu } from "./ContextMenu";
import { ListItemButton, ShowMoreButton } from "./ListItemButton";
import { useDocumentItemMenuItems } from "./useDocumentItemMenuItems";

import type { DocumentShort } from "@/api";

const RecentDocumentRow = observer(function RecentDocumentRow({ doc }: { doc: DocumentShort }) {
  const t = useTranslations();
  const { documentTree } = useStore();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const { focusWithin, focusWithinProps } = useRowFocusWithin();
  const items = useDocumentItemMenuItems({
    documentTree,
    docItem: doc,
    uid: doc.uid,
    renderContext: "recent",
    onLockedChange: (locked) => documentTree.setDocumentLocked(doc.uid, locked),
  });
  const title = doc.title || t("doc.documentTitle.placeholder");

  return (
    <MousePositionContextMenu items={items} onOpenChange={setContextMenuOpen}>
      <TreeItem
        ref={rowRef}
        focused={focusWithin}
        {...focusWithinProps}
        aria-label={title}
        onActivate={() => rowRef.current?.querySelector<HTMLElement>("a")?.click()}
        className={twMerge(
          "relative group rounded-6",
          "hover:bg-sidebar-button-hover-inverted-bg [&:has(button[data-open])]:bg-sidebar-button-hover-inverted-bg",
          contextMenuOpen && "bg-sidebar-button-hover-inverted-bg",
        )}
      >
        <ListItemButton
          as={Link}
          href={docPageUrl({ docId: doc.uid })}
          aria-current={documentTree.isCurrentDoc(doc) ? "page" : undefined}
          draggable={false}
          tabIndex={-1}
          title={title}
          className="text-document-text hover:text-document-text fromSm:group-hover:pr-7 fromSm:group-has-[:focus-visible]:pr-7 sm:pr-10"
        />
        <ContextMenu
          items={items}
          title={title}
          rowRef={rowRef}
          onOpenChange={setContextMenuOpen}
        />
      </TreeItem>
    </MousePositionContextMenu>
  );
});

export const RecentDocuments = observer(function RecentDocuments({
  className,
}: {
  className?: string;
}) {
  const t = useTranslations();
  const { documentTree } = useStore();
  const recentDocuments = useRecentDocuments();
  const teamUid = recentDocuments.team?.uid;

  useEffect(() => {
    if (!teamUid) return;

    recentDocuments.fetchWithDispose(teamUid);
  }, [teamUid, recentDocuments]);

  const documents = recentDocuments.documents.filter((doc) => !documentTree.isInTrash(doc.uid));
  const visibleDocuments = recentDocuments.isExpanded
    ? documents
    : documents.slice(0, RECENT_DOCUMENTS_COLLAPSED_COUNT);

  if (
    !teamUid ||
    documents.length === 0 ||
    documentTree.documentsCount < RECENT_DOCUMENTS_MIN_TEAM_DOCUMENTS
  ) {
    return null;
  }

  const isOpen = recentDocuments.isSectionOpen;

  return (
    <NavSection
      className={className}
      aria-label={t("documentsSidebar.recent")}
      expanded={isOpen}
      onToggle={() => recentDocuments.toggleSection()}
    >
      <SidebarSectionHeader
        isOpen={isOpen}
        onToggle={() => recentDocuments.toggleSection()}
        title={t("documentsSidebar.recent")}
        icon={<Icon name="History" width={24} height={24} className="mr-1" />}
      />
      {isOpen && (
        <>
          <SectionDivider className="my-1" />
          <ul role="group">
            {visibleDocuments.map((doc) => (
              <li key={doc.uid} role="none">
                <RecentDocumentRow doc={doc} />
              </li>
            ))}
          </ul>
          {documents.length > visibleDocuments.length && (
            <ShowMoreButton
              label={t("documentsSidebar.moreRecentDocuments")}
              onClick={() => recentDocuments.expandList()}
            />
          )}
        </>
      )}
    </NavSection>
  );
});
